import React, { HTMLAttributes, ReactNode, useCallback, useMemo } from 'react';
import { LinkField, LinkFieldValue, Link as SitecoreLink } from '@sitecore-jss/sitecore-jss-nextjs';
import NextLink from 'next/link';
import { useI18n } from 'next-localization';
import { useSitecoreContext } from 'hooks/useSitecoreContext';

type ScLinkProps = HTMLAttributes<HTMLElement> & {
  field: LinkField;
  showExternalIcon?: boolean;
  replaceWithSlot?: boolean;
  className?: string;
  children?: ReactNode;
  role?: string;
  onClick?: () => void;
  hoverEnabled?: boolean;
};

const integratedLinkStartsWith = ['http', 'mailto:', 'tel:'];

const formatQueryString = (queryString?: string) => {
  if (queryString === undefined || queryString === '') return '';
  return !queryString.startsWith('?') ? `?${queryString}` : queryString;
};

// TODO: to many rerenders of this component
const ScLink: React.FC<ScLinkProps> = ({
  field,
  showExternalIcon = true,
  replaceWithSlot = true,
  className,
  role,
  onClick = () => {},
  children,
  hoverEnabled = true,
  ...rest
}) => {
  const { t } = useI18n();
  const { sitecoreContext } = useSitecoreContext();
  const isExperienceEditor = sitecoreContext.pageEditing;
  const isRouterLink = useCallback(() => {
    if (isExperienceEditor) {
      return false;
    }
    return !integratedLinkStartsWith.find((subStr) => field?.value?.href?.startsWith(subStr));
  }, [isExperienceEditor, field?.value?.href]);
  const linkComponent: string = isRouterLink() ? 'router-link' : 'sc-link';
  const linkText: string = field?.value?.text || '';
  const queryString = formatQueryString(field?.value?.querystring);
  const isAnchorLink: boolean =
    field?.value?.href?.includes('#') || field?.value?.linkType === 'anchor';

  const hasChild: boolean = !!children;
  const hasHref = !!field?.value.href;
  const isIntegratedLink: boolean | string | undefined = useMemo(() => {
    if (isExperienceEditor) return true;
    if (isRouterLink()) return false;
    return hasChild || field?.value?.text;
  }, [hasChild, isExperienceEditor, isRouterLink, field?.value?.text]);

  const displayExternalIcon: boolean = useMemo(() => {
    if (!showExternalIcon || isExperienceEditor) return false;
    if (field?.value?.target === '_blank') return true;
    if (isIntegratedLink) {
      try {
        const siteHostName = new URL(sitecoreContext.hostName as string).host.replace('www.', '');
        const linkHostName = new URL(field?.value.href ?? '').host.replace('www.', '');
        if (siteHostName !== linkHostName) return true;
      } catch (e) {
        console.warn(
          'ScLink component failed to convert url string to URL object. Error: ',
          field.value.href
        );
        return false;
      }
    }
    return false;
  }, [
    isExperienceEditor,
    isIntegratedLink,
    field?.value.href,
    field?.value.target,
    showExternalIcon,
    sitecoreContext.hostName,
  ]);
  const opensInNewWindow: boolean = field?.value?.target === '_blank';

  const linkClass = isRouterLink() ? 'internal-link' : isIntegratedLink ? 'external-link' : '';
  const linkClassName: string = hoverEnabled
    ? `sitecore-link ${className} ${linkClass}`
    : `${className} ${linkClass}`;

  const getUtmLink = (): string | undefined => {
    // Must be legacy code as advice-uk.abrdn.com moved to JSS solution
    // const paramKeys = router && Object.keys(router?.query);
    // const utmParams = ['utm_medium', 'utm_source', 'utm_campaign', 'utm_content', 'cn'];
    // if (
    //   router &&
    //   useSitecoreContext().sitecoreContext?.site?.name === 'abrdnJssPersonal' &&
    //   value?.href.match('advice-uk.abrdn.com') &&
    //   field?.value?.linkType === 'external' &&
    //   !useSitecoreContext().sitecoreContext.pageEditing &&
    //   paramKeys.some((key) => utmParams.includes(key))
    // ) {
    //   const firstSeparator = field.value.href.includes(`?`) ? '&' : '?';
    //   let url:Array<String> = [];
    //   utmParams.forEach((param) => {
    //     if (router.query[param]) {
    //       url.push(param.replace('utm_', '') + '=' + router.query[param]);
    //     }
    //   });
    //   const splittedUrl = this.field.value.href.split('#');
    //   url = url.join('&');
    //   url = splittedUrl[0] + firstSeparator + url;
    //   url = splittedUrl[1] ? url + '#' + splittedUrl[1] : url;
    //   return url;
    // }
    return field?.value?.href;
  };

  const scLinkAttributes = (): LinkFieldValue => {
    if (isRouterLink()) {
      return isAnchorLink
        ? { href: field?.value?.href?.replace(`#${field.value?.anchor}`, ''), hash: '' }
        : { href: field?.value?.href, hash: field.value?.anchor };
    } else {
      return {
        ...field,
        value: {
          ...field?.value,
          href: getUtmLink(),
        },
      };
    }
  };

  const LinkContent = (): ReactNode => {
    const condition = (hasChild && replaceWithSlot) || isExperienceEditor;
    return (
      <>
        {condition ? (
          children
        ) : (
          <>
            {linkText}
            {children}
          </>
        )}
        {displayExternalIcon && (
          <span className="text-nowrap ms-1">
            &#65279;
            <i className="bi bi-box-arrow-up-right" />
          </span>
        )}
        {opensInNewWindow && <span className="sr-only">{t('opensInNewWindow')}</span>}
      </>
    );
  };

  if (isExperienceEditor || hasHref) {
    return (
      <>
        {linkComponent === 'router-link' && (
          <NextLink
            className={`${linkClassName} ${displayExternalIcon ? 'external-icon' : ''}`}
            href={`${field.value.href!}${queryString}`}
            target={field.value.target}
            role={role}
            onClick={onClick}
            prefetch={false}
            {...rest}
          >
            <LinkContent />
          </NextLink>
        )}
        {linkComponent === 'sc-link' && (
          <SitecoreLink
            className={linkClassName}
            field={scLinkAttributes()}
            role={role}
            onClick={onClick}
            {...rest}
          >
            <LinkContent />
          </SitecoreLink>
        )}
      </>
    );
  } else {
    return children && <div className={className}>{children}</div>;
  }
};

export default ScLink;
